import React, { useEffect, useState } from 'react'
import { Form, NavLink } from 'react-router-dom';
import { Alert, Button, Checkbox, FormControlLabel, IconButton, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import moment from 'moment';

import { IShipmentLight, IBatch } from '../../types/shipment';

import { BatchService } from '../../services/batchService';
import { ShipmentService } from '../../services/shipmentService';
import { UtilityService } from '../../services/utilityService';
import { FormatService } from '../../services/formatService';

import { useUserContext } from '../../contexts/userContext';
import { ErrorProvider } from '../../classes/errorProvider';

import Spinner from '../spinner';
import WorkingModal, { IWorkingModalProps } from '../workingModal';
import Json from '../json';
import { Icons } from '../icons';

import '../../styles/entity/batchShipmentList.scss';

export interface IBatchShipmentsForm {
	batch: IBatch;
	shipments: IShipmentLight[];
	isLoadingShipments: boolean;
	onIsProcessingChange?: (isProcessing: boolean) => void;
	onBatchShipmentsUpdate?: (shipments: IShipmentLight[]) => void;
}

export interface ICheckableShipment {
	shipment: IShipmentLight;
	checked: boolean;
	ineligibleForAddReason: string;
}

const BatchShipmentsForm: React.FunctionComponent<IBatchShipmentsForm> = ({ batch, shipments, isLoadingShipments, onIsProcessingChange, onBatchShipmentsUpdate }) => {
	const userContext = useUserContext();
	const [canEdit, setCanEdit] = useState(userContext?.permissions.canManageBatches);
	const [associatedShipments, setAssociatedShipments] = useState<ICheckableShipment[]>([]);
	const [searchResultShipments, setSearchResultShipments] = useState<ICheckableShipment[]>([]);
	const [hasSearched, setHasSearched] = useState(false);
	const [searchCriteria, setSearchCriteria] = useState<string>("");
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [isSearching, setIsSearching] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [showAddShipmentsForm, setShowAddShipmentsForm] = useState(false);
	const [selectAllAdd, setSelectAllAdd] = useState(false);
	const [selectAllRemove, setSelectAllRemove] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);

	useEffect(() => {
		if (!shipments) return;
		const checkableShipments = ShipmentService.mapShipmentLights(shipments).map((item): ICheckableShipment => { return { shipment: item, checked: false, ineligibleForAddReason: '' }; });
		checkableShipments.sort((a, b) => UtilityService.compareStrings(a.shipment.hwiNumber, b.shipment.hwiNumber));
		setAssociatedShipments(checkableShipments);		
	}, [shipments])

	useEffect(() => {
		setIsProcessing(isAdding || isRemoving);
	}, [isAdding, isRemoving])

	useEffect(() => {
		onIsProcessingChange?.(isProcessing);
	}, [isProcessing])

	const handleSearchShipments = () => {
		if (searchCriteria.trim() === "") return;
		setErrorMessage(undefined);

		setIsSearching(true);
		setSearchResultShipments([]);
		setSelectAllAdd(false);
		BatchService.searchBatchShipments(searchCriteria)
			.then((response) => {
				console.log('BatchService.searchBatchShipments: SUCCESS', response);
				const shipments = ShipmentService.mapShipmentLights(response.data).map((item): ICheckableShipment => { return { shipment: item, checked: false, ineligibleForAddReason: getAddEligibility(item) }; });
				shipments.sort((a, b) => UtilityService.compareStrings(a.shipment.hwiNumber, b.shipment.hwiNumber));
				setSearchResultShipments(shipments);
				setHasSearched(true);
			})
			.catch((error) => {
				console.log('BatchService.searchBatchShipments: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsSearching(false);
			});
	}

	// Checks if a candidate shipment to be added to the batch should be locked out being checked. Mainly for select/deselect all
	const getAddEligibility = (shipment: IShipmentLight): string => {
		if (associatedShipments.some(s => s.shipment.id === shipment.id)) return 'Already added';
		if (shipment.batchId && shipment.batchId.toString() !== batch.id.toString()) return 'In batch #' + shipment.batchId;
		if (shipment.status.toLowerCase() !== "reconciling") return 'Not "Reconciling"';
		return '';
	}

	const handleCriteriaKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSearchShipments();
		}
		else if (event.key === 'Escape') {
			event.preventDefault();
			setSearchCriteria("");
		}
	}

	const handleAddShipmentsClick = () => {
		setSearchResultShipments([]);
		setSearchCriteria("");
		setSelectAllAdd(false);
		setHasSearched(false);
		setShowAddShipmentsForm(true);
	}

	const handleAddShipmentsCancel = () => {
		setShowAddShipmentsForm(false);
	}

	const handleSubmitAddShipments = () => {
		setErrorMessage(undefined);
		const selectedShipmentIds = searchResultShipments.filter(s => s.checked).map(s => s.shipment.id);
		if (selectedShipmentIds.length > 0) {
			setIsAdding(true);
			setWorkingModalParams({
				params: {
					content: "Adding selected shipments..."
				}
			});
			BatchService.addBatchShipments(batch.id.toString(), selectedShipmentIds)
				.then((response) => {
					console.log('BatchService.addBatchShipments: SUCCESS', response);
					const addedShipmentIds: number[] = response.data;
					const addedShipments = [...searchResultShipments.filter(s => addedShipmentIds.includes(s.shipment.id))].map(s => ({ shipment: s.shipment, checked: false, ineligibleForAddReason: '' }));
					const newAssociatedShipments = [...associatedShipments, ...addedShipments].sort((a, b) => UtilityService.compareStrings(a.shipment.hwiNumber, b.shipment.hwiNumber));
					onBatchShipmentsUpdate?.(newAssociatedShipments.map(s => s.shipment));
					//setAssociatedShipments([...associatedShipments, ...addedShipments].sort((a, b) => UtilityService.compareStrings(a.shipment.bolNumber, b.shipment.bolNumber)));
					setShowAddShipmentsForm(false);
				})
				.catch((error) => {
					console.log('BatchService.addBatchShipments: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsAdding(false);
					setWorkingModalParams(undefined);
				});
		}
	}

	const handleResetSearch = () => {
		setSearchCriteria("");
		setSearchResultShipments([]);
		setSelectAllAdd(false);
		setHasSearched(false);
	}

	const handleSubmitRemoveShipments = () => {
		setErrorMessage(undefined);
		const selectedShipmentIds = associatedShipments.filter(s => s.checked).map(s => s.shipment.id);
		if (selectedShipmentIds.length > 0) {
			setIsRemoving(true);
			setWorkingModalParams({
				params: {
					content: "Removing selected shipments..."
				}
			});
			BatchService.removeBatchShipments(batch.id.toString(), selectedShipmentIds)
				.then((response) => {
					console.log('BatchService.removeBatchShipments: SUCCESS', response);
					const removedShipmentIds: number[] = response.data;
					const newAssociatedShipments = associatedShipments.filter(s => !removedShipmentIds.includes(s.shipment.id)).map(s => s.shipment);
					onBatchShipmentsUpdate?.(newAssociatedShipments);
				})
				.catch((error) => {
					console.log('BatchService.removeBatchShipments: ERROR', error);
					setErrorMessage(ErrorProvider.getError(error));
				})
				.finally(() => {
					setIsRemoving(false);
					setWorkingModalParams(undefined);
					setSelectAllRemove(false);
				});
		}
	}

	const handleResetRemoveSelections = () => {
		checkAssociatedShipment(undefined, false);
	}

	// Toggle the "Remove" checkbox for associated shipments. Undefined index means all, undefined checkedValue means flip.
	const checkAssociatedShipment = (index?: number, checkedValue?: boolean) => {
		let shipments: ICheckableShipment[];
		if (index !== undefined) {
			shipments = [...associatedShipments];
			shipments[index].checked = checkedValue === undefined ? !shipments[index].checked : checkedValue;
			setAssociatedShipments(shipments);
		} else {
			shipments = associatedShipments.map(item => ({ ...item, checked: checkedValue === undefined ? !item.checked : checkedValue }));
			setAssociatedShipments(shipments);
		}
		setSelectAllRemove(shipments.length > 0 && shipments.every(item => item.checked));
	}

	const handleSelectAllRemoveClick = () => {
		checkAssociatedShipment(undefined, !selectAllRemove);
	}

	// Toggle the "Add" checkbox for associated shipments. Undefined index means all, undefined checkedValue means flip.
	const checkSearchResultsShipment = (index?: number, checkedValue?: boolean) => {
		let shipments: ICheckableShipment[];
		if (index !== undefined) {
			shipments = [...searchResultShipments];
			if (!shipments[index].ineligibleForAddReason) {
				shipments[index].checked = checkedValue === undefined ? !shipments[index].checked : checkedValue;
			}
			setSearchResultShipments(shipments);
		} else {
			shipments = searchResultShipments.map(item => {
				// Don't modify the item if it's ineligible to be added to the batch
				return item.ineligibleForAddReason ? item : { ...item, checked: checkedValue === undefined ? !item.checked : checkedValue };
			});
			setSearchResultShipments(shipments);
		}

		// Only consider eligible shipments when determining what "all" means
		setSelectAllAdd(shipments.filter(item => !item.ineligibleForAddReason).length > 0 && shipments.filter(item => !item.ineligibleForAddReason).every(item => item.checked));
	}

	const handleSelectAllAddClick = () => {
		checkSearchResultsShipment(undefined, !selectAllAdd);
	}

	return (
		<section className="hwi-batch-shipment-list">
			<Spinner dependency={isLoadingShipments} text="Loading associated shipments...">
				{batch.status != "Reconciling" &&
					<Alert className="alert-error" severity="info" >
						This batch is in <strong>{batch.status}</strong> status. You may only add or remove shipments in <strong>Reconciling</strong> status.
					</Alert>
				}
				{(!showAddShipmentsForm && errorMessage) &&
					<div className="error">
						<Alert className="alert-error"
							severity="error"
							action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}>
							{errorMessage}
						</Alert>
					</div>
				}
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					{showAddShipmentsForm ? (
						<>
							<div className="shipment-search">
								<div className="controls">
									<div className="criteria-container">
										<TextField className="criteria-field"
											name="criteria"
											fullWidth={true}
											value={searchCriteria}
											multiline={false}
											rows={1}
											placeholder='Search by BOL, invoice, or manifest number.'
											disabled={isSearching || isAdding}
											onChange={(event) => { setSearchCriteria(event.target.value) }}
											onKeyDown={handleCriteriaKeyDown}
										>
										</TextField>
									</div>
									<div className="actions">
										<Button variant="contained" type="button" disabled={isSearching || isAdding} onClick={() => handleSearchShipments()}>{isSearching ? "Searching..." : "Search"}</Button>
									</div>
								</div>
								<div className="help-text">
									<div className="criteria-container">Separate multiple search criteria with commas. Only "Reconciling" shipments can be added to a batch.</div>
									<div className="actions"></div>
								</div>
							</div>
							<Form onSubmit={() => handleSubmitAddShipments()}>
								<div className="hwi-entity-search-results-table">
									<div className="headers">
										<div className="col-bol">BOL#</div>
										<div className="col-customer">Customer</div>
										<div className="col-vendor">Vendor</div>
										<div className="col-plant-wt">Plant Wt</div>
										<div className="col-vendor-wt">Vendor Wt</div>
										<div className="col-shipped">Shipped</div>
										<div className='col-status'>Status</div>
										<div className="col-select-all">
											<span className='select-all'>
												<FormControlLabel control={<Checkbox size='small' checked={selectAllAdd} onChange={handleSelectAllAddClick}></Checkbox>} label="Select All" />
											</span>
										</div>
									</div>
									<div className="data">
										{searchResultShipments.map((item, index) => (
											<div className='row' key={index}>
												<div className="col-bol"><p>{item.shipment.hwiNumber}</p></div>
												<div className="col-customer"><p>{item.shipment.customerName}</p></div>
												<div className="col-vendor"><p>{item.shipment.vendorName}</p></div>
												<div className="col-plant-wt"><p>{FormatService.weight(item.shipment.plantWeight, 4)}</p></div>
												<div className="col-vendor-wt"><p>{FormatService.weight(item.shipment.vendorWeight, 4)}</p></div>
												<div className="col-shipped"><p>{item.shipment.pickupDate ? moment(item.shipment.pickupDate).format("MM/DD/yyyy") : ""}</p></div>
												<div className="col-status"><p>{item.shipment.status}</p></div>
												<div className="col-select-all">
													{item.ineligibleForAddReason &&
														<p className="ineligibility-reason">
															{item.ineligibleForAddReason}
														</p>
													}
													{!item.ineligibleForAddReason &&
														<p>
															<FormControlLabel control={<Checkbox size='small' checked={item.checked} onChange={() => checkSearchResultsShipment(index)}></Checkbox>} label="Add" />
														</p>
													}
												</div>
											</div>
										))}
										{isSearching &&
											<Spinner dependency={isSearching} text="Searching Shipments..."></Spinner>
										}
										{!isSearching && hasSearched && searchResultShipments.length == 0 &&
											<div className="no-results">Search returned no shipments.</div>
										}
										{!isSearching && !hasSearched &&
											<div className="no-results">Enter BOL or Invoice numbers to search for shipments.</div>
										}
									</div>
									<div className="add-selection-row">
										{searchResultShipments.length > 0 &&
											<>
												<Button type="submit" className="save-button" variant="contained" disabled={isAdding || searchResultShipments.every(s => !s.checked)}>{isAdding ? "Adding..." : "Add Selected"}</Button>
												{" "}
												<Button variant='contained' color="error" disabled={isAdding} onClick={() => handleResetSearch()}>Reset Search</Button>
												{" "}
											</>
										}
										<Button className="cancel-button" variant="outlined" disabled={isAdding} onClick={handleAddShipmentsCancel}>Cancel</Button>
									</div>
								</div>
								{/* <Json data={newMaterial} title="New Material"></Json> */}
							</Form>
						</>
					) : (
						<>
							<Form onSubmit={() => handleSubmitRemoveShipments()}>
								<div className="hwi-entity-search-results-table">
									<div className="headers">
										<div className="col-bol">BOL#</div>
										<div className="col-customer">Customer</div>
										<div className="col-vendor">Vendor</div>
										<div className="col-plant-wt">Plant Wt</div>
										<div className="col-vendor-wt">Vendor Wt</div>
										<div className="col-shipped">Shipped</div>
										<div className="col-select-all">
											{batch.status == "Reconciling" &&
												<span className='select-all'>
													<FormControlLabel control={<Checkbox size='small' checked={selectAllRemove} disabled={!canEdit} onChange={handleSelectAllRemoveClick}></Checkbox>} label="Select All" />
												</span>
											}
										</div>
										<div className="col-controls"></div>
									</div>
									<div className="data">
										{associatedShipments.map((item, index) => (
											<div className='row' key={index}>
												<div className="col-bol"><p>{item.shipment.hwiNumber}</p></div>
												<div className="col-customer"><p>{item.shipment.customerName}</p></div>
												<div className="col-vendor"><p>{item.shipment.vendorName}</p></div>
												<div className="col-plant-wt"><p>{FormatService.weight(item.shipment.plantWeight, 4)}</p></div>
												<div className="col-vendor-wt"><p>{FormatService.weight(item.shipment.vendorWeight, 4)}</p></div>
												<div className="col-shipped"><p>{item.shipment.pickupDate ? moment(item.shipment.pickupDate).format("MM/DD/yyyy") : ""}</p></div>
												<div className="col-select-all">
													{batch.status == "Reconciling" &&
														<p>
															<FormControlLabel control={<Checkbox size='small' checked={item.checked} disabled={!canEdit} onChange={() => checkAssociatedShipment(index)}></Checkbox>} label="Remove" />
														</p>
													}
												</div>
												<div className="col-controls">
													<p>
														<NavLink to={"/shipment/" + item.shipment.id}>
															<Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>
															{" "}
															Open
														</NavLink>
													</p>
												</div>
											</div>
										))}
										{associatedShipments.length == 0 &&
											<div className="no-results">This batch contains no shipments.</div>
										}
									</div>
									<div className='remove-selection-row'>
										{batch.status == "Reconciling" &&
											<>
												<Button type="button" className="save-button" variant="contained" disabled={!canEdit || isRemoving} onClick={handleAddShipmentsClick}>Add Shipments</Button>
												{associatedShipments.length > 0 &&
													<>
														<Button type="submit" className="remove-button" variant="contained" color="error" disabled={!canEdit || isRemoving || associatedShipments.every(s => !s.checked)}>{isRemoving ? "Removing..." : "Remove Selected"}</Button>
														<Button variant='outlined' color="error" disabled={!canEdit || isRemoving} onClick={handleResetRemoveSelections}>Reset Selections</Button>
													</>
												}
											</>
										}
									</div>
								</div>
							</Form>
						</>
					)}
				</LocalizationProvider>
			</Spinner>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
			{/* <Json data={associatedShipments}></Json> */}
		</section>
	)
}

export default BatchShipmentsForm;